import React, { useEffect, useState } from "react";
import { Alert, Button, Empty, Form, Input, Modal, Popconfirm, Space, Table, Tag, message } from "antd";
import { financeService } from "../../services/financeService";
import type { StockCategory } from "../../types";

interface StockCategoryModalProps {
  open: boolean;
  categories: StockCategory[];
  onClose: () => void;
  /** Reload the parent list; a created category is returned so the item form can select it. */
  onChanged: (createdCode?: string) => Promise<void> | void;
}

/**
 * Category master for the stock item form.
 *
 * Codes are generated and immutable; the user edits the display name. That
 * keeps existing stock items, movements and the 5100/5200/5300/5400 cost
 * mapping stable when a category is renamed. Categories are never hard-deleted
 * — an item may still point at them — only deactivated and reactivated.
 */
const StockCategoryModal: React.FC<StockCategoryModalProps> = ({
  open,
  categories,
  onClose,
  onChanged,
}) => {
  const [form] = Form.useForm();
  const [editing, setEditing] = useState<StockCategory | null>(null);
  const [saving, setSaving] = useState(false);
  const [updatingCode, setUpdatingCode] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setEditing(null);
      form.resetFields();
    }
  }, [open, form]);

  const submit = async () => {
    let values: { name: string };
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await financeService.updateStockCategory(editing.code, { name: values.name });
        message.success("Category updated");
        setEditing(null);
        form.resetFields();
        await onChanged();
      } else {
        const created = await financeService.createStockCategory({ name: values.name });
        message.success(`Category ${created.name} created`);
        form.resetFields();
        await onChanged(created.code);
      }
    } catch (e: any) {
      message.error(e?.message || "Failed to save category");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (category: StockCategory) => {
    setUpdatingCode(category.code);
    try {
      await financeService.updateStockCategory(category.code, { is_active: !category.is_active });
      message.success(category.is_active ? "Category deactivated" : "Category reactivated");
      await onChanged();
    } catch (e: any) {
      message.error(e?.message || "Failed to update category");
    } finally {
      setUpdatingCode(null);
    }
  };

  return (
    <Modal
      open={open}
      title="Manage stock categories"
      onCancel={onClose}
      footer={null}
      width={680}
      destroyOnClose
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        title="Renaming does not change history"
        description="A category keeps its internal code when renamed, so existing purchases and stock movements keep their original cost classification. Custom categories post to 5400 Other Direct Costs."
      />

      <Form form={form} layout="inline" onFinish={submit} style={{ marginBottom: 16, gap: 8 }}>
        <Form.Item
          name="name"
          rules={[{ required: true, message: "Enter a category name" }]}
          style={{ flex: 1, marginRight: 8 }}
        >
          <Input
            maxLength={80}
            placeholder={editing ? `Rename ${editing.name}` : "New category, e.g. Dry goods"}
          />
        </Form.Item>
        <Form.Item style={{ marginRight: 0 }}>
          <Space>
            <Button type="primary" loading={saving} onClick={submit}>
              {editing ? "Save" : "Add category"}
            </Button>
            {editing && (
              <Button
                onClick={() => {
                  setEditing(null);
                  form.resetFields();
                }}
              >
                Cancel
              </Button>
            )}
          </Space>
        </Form.Item>
      </Form>

      <Table
        rowKey="code"
        size="small"
        dataSource={categories}
        pagination={false}
        locale={{ emptyText: <Empty description="No categories yet" /> }}
        columns={[
          { title: "Category", dataIndex: "name", key: "name" },
          {
            title: "Items",
            key: "items",
            width: 90,
            align: "right" as const,
            render: (_: any, row: StockCategory) => row.item_count ?? 0,
          },
          {
            title: "State",
            dataIndex: "is_active",
            key: "is_active",
            width: 100,
            render: (active: boolean) =>
              active ? <Tag color="success">Active</Tag> : <Tag>Inactive</Tag>,
          },
          {
            title: "",
            key: "actions",
            width: 180,
            render: (_: any, row: StockCategory) => (
              <Space size={4}>
                <Button
                  size="small"
                  onClick={() => {
                    setEditing(row);
                    form.setFieldsValue({ name: row.name });
                  }}
                >
                  Rename
                </Button>
                <Popconfirm
                  title={row.is_active ? `Deactivate ${row.name}?` : `Reactivate ${row.name}?`}
                  description={
                    row.is_active
                      ? "Existing items keep this category; it will no longer be selectable for new items."
                      : "The category will be selectable again for new items."
                  }
                  onConfirm={() => toggleActive(row)}
                >
                  <Button size="small" loading={updatingCode === row.code}>
                    {row.is_active ? "Deactivate" : "Reactivate"}
                  </Button>
                </Popconfirm>
              </Space>
            ),
          },
        ]}
      />
    </Modal>
  );
};

export default StockCategoryModal;
